'use client';
import { useEffect } from 'react';
import { useAtom, useDidUpdate } from '@s7/hooks';
import { atomicFetch } from '../atomicFetch.js';
import { defaultResponseTransform } from '../defaultResponseTransform.js';
import { DEFAULT_CLIENT, DEFAULT_CACHE } from '../defaults.js';
import { Refresh } from './domain.js';
import { useInteractivityController } from './useInteractivityController.js';
import { requestHash } from '../../utils/requestHash/requestHash.js';
import { useInvalidationSubscriber } from '../invalidation/useInvalidationSubscriber.js';
import { handleErrorTransform } from '../../handleErrorTransform/handleErrorTransform.js';

function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
const getStrategyFlags = (strategy, skip)=>{
    const withEffects = strategy === Refresh.OnMountsAndQueryChanges;
    const withMountEffect = !skip && (withEffects || strategy === Refresh.OnMount);
    const withUpdateEffect = !skip && (withEffects || strategy === Refresh.OnQueryChanges);
    return [
        withMountEffect,
        withUpdateEffect
    ];
};
const useQueryImpl = (options, __quid)=>{
    const { query, transform = defaultResponseTransform, transformError, persist, skip, refresh = Refresh.OnMountsAndQueryChanges, interactive = true, onSuccess, onError, onFinish, client = DEFAULT_CLIENT, cache = DEFAULT_CACHE } = options;
    /// DERIVATIONS
    const hash = requestHash(query);
    const [watchMounts, watchUpdates] = getStrategyFlags(refresh, skip);
    const initialData = skip || cache.clientOnly ? null : cache.get(hash);
    /// STATE
    const [isInteractivityDisabled, disableInteractivity] = useInteractivityController();
    const [loading, setLoading] = useAtom(`l-${hash}`, watchMounts);
    const [data, setData] = useAtom(`d-${hash}`, initialData);
    const [error, setError] = useAtom(`e-${hash}`, null);
    /// LOGIC
    const hiddenUpdate = /*#__PURE__*/ _async_to_generator(function*() {
        try {
            const response = yield atomicFetch(hash, client, query);
            const data = yield transform(response.clone());
            setError(null);
            setData(data);
            if (persist) {
                cache.set(hash, data);
            }
            disableInteractivity();
            onSuccess == null ? void 0 : onSuccess(data);
        } catch (error) {
            const finalError = yield handleErrorTransform(error, transformError);
            setError(finalError);
            onError == null ? void 0 : onError(finalError);
        } finally{
            onFinish == null ? void 0 : onFinish();
        }
    });
    const interactiveUpdate = /*#__PURE__*/ _async_to_generator(function*() {
        setLoading(true);
        setData(null);
        yield hiddenUpdate();
        setLoading(false);
    });
    const update = /*#__PURE__*/ _async_to_generator(function*() {
        if (isInteractivityDisabled()) {
            yield (interactive ? interactiveUpdate : hiddenUpdate)();
        } else {
            yield interactiveUpdate();
        }
    });
    const restoreStateFromCache = ()=>{
        const data = cache.get(hash);
        if (!data) {
            return;
        }
        setData(data);
        setLoading(false);
        disableInteractivity();
        return data;
    };
    const restoreOrRefresh = ()=>{
        if (persist) {
            !restoreStateFromCache() && update();
        } else {
            update();
        }
    };
    const reset = ()=>{
        setLoading(false);
        setError(null);
        setData(null);
    };
    /// FX
    useEffect(()=>{
        if (cache.clientOnly) {
            const data = restoreStateFromCache();
            if (!data && watchMounts) {
                restoreOrRefresh();
            }
        } else if (watchMounts) {
            restoreOrRefresh();
        }
    }, [
        watchMounts
    ]);
    useDidUpdate(()=>{
        if (watchUpdates) {
            restoreOrRefresh();
        }
    }, [
        hash,
        watchUpdates
    ]);
    useInvalidationSubscriber(__quid, persist, data, update);
    return [
        data,
        {
            loading,
            error,
            refresh: update,
            reset,
            restoreOrRefresh
        }
    ];
};

export { useQueryImpl };
//# sourceMappingURL=useQuery.js.map
